import { Typography } from '@interstate/components/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ContainerGrid,
    ContainerGridItem,
    SellRateField,
    BuyRateField,
    CreditDecisionLenderNameField,
    CreditDecisionStatusField,
    CreditBureau,
    PaymentLoading,
    LenderStreetAddressField,
    LenderStreetAddress2Field,
    LenderCityField,
    LenderStateField,
    LenderZipField,
    LenderPhoneField,
    LenderAddressValidationProvider,
    LENDER_ADDRESS_SECTION_LABEL,
    CancellationDaysField,
    CancellationChargeField,
    ReserveSplitField,
    ReserveInformation
} from '@makemydeal/dr-dash-components';
import { LoadingSectionParent } from '@makemydeal/dr-shared-components';
import { useDispatch, useSelector } from 'react-redux';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as lenderAddressSelectors from '@makemydeal/dr-dash-store/selectors/lenderAddressSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { Box } from '@interstate/components/Box';
import { useFormValidationWithContractFlow } from '@makemydeal/dr-dash-components';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';

import { DealTabs } from '@makemydeal/dr-shared-types';
import LenderProbabilities from './LenderComparison/LenderProbabilities';
import { OfferTypeEnum } from '@makemydeal/dr-platform-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { Alert } from '@interstate/components/Alert';
import { ViewLenderDecisionLink } from './ViewLenderDecisionLink';

import {
    lenderValidationConfig,
    LENDER_ADDRESS_FIELD_PATHS
} from '@makemydeal/dr-shared-ui-utils/validationConfigs/lenderValidationConfig';
import { cancellationTermsValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/cancellationTermsValidationConfig';
import { reserveValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/reserveValidationConfig';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';
import { Action } from '@interstate/components/Action';
import { Interstitial as LoadingIndicator } from '@interstate/components/Interstitial';
import {
    LENDER_ADDRESS_LOAD_ERROR_PREFIX_MESSAGE,
    LENDER_ADDRESS_LOAD_ERROR_REFRESH,
    LENDER_ADDRESS_LOAD_ERROR_SUFFIX_MESSAGE
} from '../../constants';
import { LoaderWrapper } from './BaseDealLender.style';
import { fetchLenderAddress } from '@makemydeal/dr-dash-store/actions/lenderActionCreators';

type BaseDealLenderProps = {
    isInsideSlideOut?: boolean;
    isLenderFormDirtyRef?: React.MutableRefObject<boolean>;
};

const BaseDealLender = ({ isInsideSlideOut = false, isLenderFormDirtyRef }: BaseDealLenderProps) => {
    const dispatch = useDispatch();
    const lenderWithProfitEnabled = useSelector(featureToggleSelectors.isLenderComparisonWithProfitEnabled);
    const isLenderComparisonMVEnabled = useSelector(featureToggleSelectors.isLenderComparisonMVEnabled);
    const lenderProbabilities = useSelector(deskingSelectors.getLenderProbabilities);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isBreakdownOpen = useSelector(baseDealSelectors.getBreakdownStatus);
    const isMvLiteEnabled = useSelector(dealerSelectors.isMvLiteEnabled);
    const activeTab = useSelector(baseDealSelectors.getActiveTab);
    const lender = useSelector(offerReduxSelectors.getCreditDecisionLender);
    const dealScreenRedDotLenderAccumulatedErrors = useSelector((state: StateTree) =>
        dealScreenErrorSelectors.getEntityValidationErrors(state, DealTabs.LENDER)
    );
    const titlingLenderWithAddress = useSelector(offerReduxSelectors.getTitlingLenderWithAddress);
    const showLenderAddressSection = useSelector(featureToggleSelectors.enableLenderAddress);
    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
    const isLenderAddressLoading = useSelector(lenderAddressSelectors.getIsLenderAddressLoading);
    const hasLenderAddressError = useSelector(lenderAddressSelectors.getHasLenderAddressError);

    const lenderValidator = useFormValidationWithContractFlow({
        dispatchMode: 'onError',
        initialConfig: lenderValidationConfig
    });
    const [formErrors, setFormErrors] = useState(() => lenderValidator.getFormErrorState({ refresh: false }));

    const hasContractingErrors = useMemo(lenderValidator.getHasContractErrors, [formErrors, isContractingContext, lenderValidator]);
    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);
    const gridItemMdWidth = isBreakdownOpen || isDealCentralMultiScenario ? 6 : 3;
    const isFinanceOffer = offerType === OfferTypeEnum.FINANCE;
    const lenderData = lenderProbabilities?.length && isFinanceOffer ? lenderProbabilities : false;
    const showSoftPullPanel = isLenderComparisonMVEnabled;

    const isReserveSplitOverrideEnabled = useSelector(featureToggleSelectors.isReserveSplitOverrideEnabled);

    const isRightToCancelEnabled = useSelector(featureToggleSelectors.enableRightToCancelFields);
    const dealerState = useSelector(dealerSelectors.getDealerStateCode);
    const cancellationApplicableStates = useSelector(configSelectors.getCancellationApplicableStates);
    const showCancellationFields = isRightToCancelEnabled && cancellationApplicableStates.includes(dealerState ?? '');
    const isLocked = dealLockHooks.useIsDealLocked();

    const cancellationValidator = useFormValidationWithContractFlow({
        dispatchMode: 'onError',
        initialConfig: cancellationTermsValidationConfig
    });
    const [cancellationFormErrors, setCancellationFormErrors] = useState(() =>
        cancellationValidator.getFormErrorState({ refresh: false })
    );

    const reserveValidator = useFormValidationWithContractFlow({
        dispatchMode: 'onError',
        initialConfig: reserveValidationConfig
    });
    const [reserveFormErrors, setReserveFormErrors] = useState(() => reserveValidator.getFormErrorState({ refresh: false }));

    const onReserveSplitBlur = (value: number | undefined) => {
        const safeValue = value ?? null;
        reserveValidator.ctx.values = { reserveSplit: safeValue };
        reserveValidator.validatePath('reserveSplit', safeValue);
        setReserveFormErrors(reserveValidator.getFormErrorState({ refresh: false }));
    };

    const rightToCancelDays = useSelector(baseDealSelectors.getRightToCancelDays);
    const rightToCancelCharge = useSelector(baseDealSelectors.getRightToCancelCharge);

    useEffect(() => {
        cancellationValidator.ctx.values = {
            rightToCancelDays: rightToCancelDays ?? null,
            rightToCancelCharge: rightToCancelCharge ?? null
        };
    }, [rightToCancelDays, rightToCancelCharge]);

    const onCancellationFieldBlur = (field: 'rightToCancelDays' | 'rightToCancelCharge', value: number | undefined) => {
        const safeValue = value ?? null;
        cancellationValidator.ctx.values = {
            rightToCancelDays: field === 'rightToCancelDays' ? safeValue : rightToCancelDays ?? null,
            rightToCancelCharge: field === 'rightToCancelCharge' ? safeValue : rightToCancelCharge ?? null
        };
        cancellationValidator.validatePath(field, safeValue);
        const errorState = cancellationValidator.getFormErrorState({ refresh: false });
        setCancellationFormErrors(errorState);

        if (isContractingContext) {
            dispatch(contractingActionCreators.refreshContractValidationErrors());
        }
    };

    const validateLenderAddressFields = useCallback(() => {
        LENDER_ADDRESS_FIELD_PATHS.forEach((path) => {
            lenderValidator.setValue(path, (titlingLenderWithAddress as Record<string, string | null> | undefined)?.[path] ?? '', {
                validate: true
            });
        });
        setFormErrors(lenderValidator.getFormErrorState({ refresh: false }));
    }, [titlingLenderWithAddress, lenderValidator]);

    useEffect(() => {
        lenderValidator.ctx.values = { ...lenderValidator.ctx.values, name: lender?.name || '' };
        lenderValidator.ctx.offerType = offerType;
    }, [lender?.name, offerType]);

    useEffect(() => {
        if (!isContractingContext) {
            lenderValidator.clearErrors('name');
            setFormErrors(lenderValidator.getFormErrorState({ refresh: false }));
        }
    }, [isContractingContext, lenderValidator]);

    useEffect(() => {
        if (isContractingContext) {
            lenderValidator.validatePath('name', lender?.name || '');
            setFormErrors(lenderValidator.getFormErrorState({ refresh: false }));
        }
    }, [activeTab, isContractingContext, lender?.name]);

    // Keep address inline errors in sync with the stored address and whenever the contract flow
    // toggles (setFlow clears the engine's error cache, so we re-validate here).
    useEffect(() => {
        if (showLenderAddressSection) {
            validateLenderAddressFields();
        }
    }, [showLenderAddressSection, isContractingContext, validateLenderAddressFields]);

    // The contract validation snapshot reads committed (Redux) address values, which only update on
    // blur soft-save — not on change. Re-run it whenever the committed titling address changes so an
    // invalid value surfaces (and a corrected one clears) as soon as the field is blurred, rather
    // than one edit later. `titlingLenderWithAddress` is a stable reference that only changes on save.
    useEffect(() => {
        if (showLenderAddressSection && isContractingContext) {
            dispatch(contractingActionCreators.refreshContractValidationErrors());
        }
    }, [titlingLenderWithAddress, showLenderAddressSection, isContractingContext, dispatch]);

    // Only dispatch when a field's error state actually changed vs. what's already stored. This
    // preserves the original `name` red-dot behavior (no per-keystroke churn, no no-op dispatch on
    // mount) while still letting address field errors drive the lender tab red dot.
    useEffect(() => {
        const nextErrorFields: Record<string, boolean> = { name: formErrors.name.hasError };
        LENDER_ADDRESS_FIELD_PATHS.forEach((path) => {
            nextErrorFields[path] = Boolean(formErrors[path]?.hasError);
        });

        const hasChanged = Object.entries(nextErrorFields).some(
            ([field, hasError]) => Boolean(dealScreenRedDotLenderAccumulatedErrors[field]) !== hasError
        );
        if (hasChanged) {
            dispatch(dealScreenErrorActionCreators.setErrorFields('lender', nextErrorFields));
        }
    }, [formErrors, dealScreenRedDotLenderAccumulatedErrors]);

    const contractFieldValidationOnChange = (name: string = '') => {
        if (isContractingContext) {
            lenderValidator.ctx.values = { ...lenderValidator.ctx.values, name: name || '' };
            lenderValidator.ctx.offerType = offerType;
            lenderValidator.validatePath('name', name || '');

            const errorState = lenderValidator.getFormErrorState({ refresh: false });
            setFormErrors(errorState);

            if (!errorState.name.hasError) {
                dispatch(contractingActionCreators.refreshContractValidationErrors());
            }
        }
    };

    const onAddressFieldValidation = (path: string, value: string) => {
        lenderValidator.setValue(path, value, { validate: true });
        const errorState = lenderValidator.getFormErrorState({ refresh: false });
        setFormErrors(errorState);

        if (isContractingContext && !errorState[path]?.hasError) {
            dispatch(contractingActionCreators.refreshContractValidationErrors());
        }
    };

    const leaseFields = [
        {
            component: (
                <CreditDecisionLenderNameField
                    isInLenderPanel
                    onChangeContractFieldValidation={contractFieldValidationOnChange}
                    error={formErrors.name}
                />
            ),
            key: 'CreditDecisionLenderNameField'
        },
        { component: <CreditDecisionStatusField />, key: 'CreditDecisionStatusField' },
        { component: <BuyRateField tabName={DealTabs.LENDER} />, key: 'BuyRateField' },
        { component: <SellRateField tabName={DealTabs.LENDER} />, key: 'SellRateField' }
    ];

    return (
        <LoadingSectionParent>
            <PaymentLoading testId="loading-indicator" />
            <Box>
                {showLenderAddressSection && isLenderAddressLoading && (
                    <LoaderWrapper data-testid="titling-lender-address-loading">
                        <LoadingIndicator size="small" />
                    </LoaderWrapper>
                )}
                <LenderProbabilities
                    showProfit={lenderWithProfitEnabled}
                    lenderData={lenderData}
                    isInsideSlideOut={isInsideSlideOut}
                />
                {showLenderAddressSection && hasLenderAddressError && lender?.id && (
                    <Alert type="error" sx={{ marginBottom: '20px' }} data-testid="titling-lender-address-alert">
                        <Typography variant="h6" sx={{ marginBottom: '2px' }}>
                            {LENDER_ADDRESS_LOAD_ERROR_PREFIX_MESSAGE}
                        </Typography>
                        <Typography variant="body-sm">
                            <Action
                                data-testid="titling-lender-address-refresh-btn"
                                size="sm"
                                onClick={() => dispatch(fetchLenderAddress(lender.id, { forceRefresh: true }))}
                            >
                                {LENDER_ADDRESS_LOAD_ERROR_REFRESH}
                            </Action>
                            &nbsp;{LENDER_ADDRESS_LOAD_ERROR_SUFFIX_MESSAGE}
                        </Typography>
                    </Alert>
                )}
                <Box display="flex" justifyContent="space-between" mb={3}>
                    <Typography data-testid="lender-title" variant="h3">
                        Lender Information
                    </Typography>
                    {/* Hide "View Lender Decisions" CTA in MV Lite mode */}
                    {!isMvLiteEnabled && <ViewLenderDecisionLink isInsideSlideOut={isInsideSlideOut} />}
                </Box>

                {hasContractingErrors && (
                    <Alert type="error" sx={{ margin: '1rem 0' }}>
                        Cannot generate contract. Please fix the highlighted lender details before continuing.
                    </Alert>
                )}

                <ContainerGrid data-testid="base-deal-form-fields" $alignItems="start">
                    {leaseFields.map(({ key, component }) => (
                        <ContainerGridItem key={key} xs={6} md={gridItemMdWidth} data-testid={`base-deal-form-fields-${key}`}>
                            {component}
                        </ContainerGridItem>
                    ))}
                    {isReserveSplitOverrideEnabled && (
                        <ContainerGridItem xs={6} md={gridItemMdWidth} data-testid="base-deal-form-fields-ReserveSplitField">
                            <ReserveSplitField
                                hasError={reserveFormErrors.reserveSplit?.hasError}
                                errorMessage={reserveFormErrors.reserveSplit?.errorMessage}
                                onBlur={onReserveSplitBlur}
                            />
                        </ContainerGridItem>
                    )}
                    {showCancellationFields && (
                        <>
                            <ContainerGridItem xs={6} md={gridItemMdWidth} data-testid="cancellationDaysField">
                                <CancellationDaysField
                                    disabled={isLocked}
                                    hasError={cancellationFormErrors.rightToCancelDays?.hasError}
                                    errorMessage={cancellationFormErrors.rightToCancelDays?.errorMessage}
                                    onBlur={(value) => onCancellationFieldBlur('rightToCancelDays', value)}
                                />
                            </ContainerGridItem>
                            <ContainerGridItem xs={6} md={gridItemMdWidth} data-testid="cancellationChargeField">
                                <CancellationChargeField
                                    disabled={isLocked}
                                    hasError={cancellationFormErrors.rightToCancelCharge?.hasError}
                                    errorMessage={cancellationFormErrors.rightToCancelCharge?.errorMessage}
                                    onBlur={(value) => onCancellationFieldBlur('rightToCancelCharge', value)}
                                />
                            </ContainerGridItem>
                        </>
                    )}
                </ContainerGrid>
                {isReserveSplitOverrideEnabled && <ReserveInformation />}
                {showLenderAddressSection && (
                    <Box
                        data-testid="lender-address-section"
                        sx={{
                            backgroundColor: ({ tokens }) => tokens.BaseColorGray50,
                            borderRadius: ({ tokens }) => tokens.BaseSizeBorderRadiusSm,
                            padding: 2,
                            marginTop: 2
                        }}
                    >
                        <Typography data-testid="lender-address-section-title" variant="h5" sx={{ marginBottom: 1.5 }}>
                            {LENDER_ADDRESS_SECTION_LABEL}
                        </Typography>
                        <LenderAddressValidationProvider
                            formErrors={formErrors}
                            onAddressFieldValidation={onAddressFieldValidation}
                        >
                            <ContainerGrid data-testid="titling-lender-address-form-fields" $alignItems="start">
                                <ContainerGridItem xs={6} md={gridItemMdWidth} data-testid="lenderStreetAddressField">
                                    <LenderStreetAddressField />
                                </ContainerGridItem>
                                <ContainerGridItem xs={6} md={gridItemMdWidth} data-testid="lenderStreetAddress2Field">
                                    <LenderStreetAddress2Field />
                                </ContainerGridItem>
                                <ContainerGridItem xs={6} md={gridItemMdWidth} data-testid="lenderCityField">
                                    <LenderCityField />
                                </ContainerGridItem>
                                <ContainerGridItem xs={6} md={gridItemMdWidth} data-testid="lenderStateField">
                                    <LenderStateField />
                                </ContainerGridItem>
                                <ContainerGridItem xs={6} md={gridItemMdWidth} data-testid="lenderZipField">
                                    <LenderZipField />
                                </ContainerGridItem>
                                <ContainerGridItem xs={6} md={gridItemMdWidth} data-testid="lenderPhoneField">
                                    <LenderPhoneField />
                                </ContainerGridItem>
                            </ContainerGrid>
                        </LenderAddressValidationProvider>
                    </Box>
                )}
                <CreditBureau isLenderFormDirtyRef={isLenderFormDirtyRef} showSoftPullPanel={showSoftPullPanel} />
            </Box>
        </LoadingSectionParent>
    );
};

export default BaseDealLender;
